import { Box, SxProps, Theme } from "@mui/material";
import { motion, usePresenceData } from "motion/react";
import React, { Children } from "react";

import { AnimatePresence } from "./AnimatePresence";
import { mergeSx } from "../utils";

interface SlideStackProps {
  direction?: "left" | "right";
  children: React.ReactNode;
  sx?: SxProps<Theme>;
}

/**
 * Stacks its children on top of each other. When a child is added it slides
 * in; when removed it slides back out.
 * Children present at mount are rendered without animation.
 * Each child must have a stable key.
 */
export const SlideStack = ({
  direction = "right",
  children,
  sx,
}: SlideStackProps) => {
  const childArray = Children.toArray(children).filter(React.isValidElement);

  return (
    <Box
      sx={mergeSx(
        {
          position: "relative",
          height: "100%",
          overflow: "hidden",
        },
        sx
      )}
    >
      <AnimatePresence>
        {childArray.map((child) => (
          <SlideItem key={child.key} direction={direction}>
            {child}
          </SlideItem>
        ))}
      </AnimatePresence>
    </Box>
  );
};

const SlideItem = ({
  direction,
  children,
}: {
  direction: "left" | "right";
  children: React.ReactNode;
}) => {
  const initialRender = usePresenceData() as boolean;

  const x = direction === "left" ? "-100%" : "100%";
  return (
    <motion.div
      initial={initialRender ? false : { x }}
      animate={{ x: 0 }}
      exit={{ x }}
      transition={{ type: "spring", bounce: 0, duration: 0.3 }}
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {children}
    </motion.div>
  );
};
